import { EmailHistory } from '../email/EmailHistory';
import BlockRounded from '@mui/icons-material/BlockRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import EventRounded from '@mui/icons-material/EventRounded';
import InventoryRounded from '@mui/icons-material/InventoryRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import MoveToInboxRounded from '@mui/icons-material/MoveToInboxRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import { Alert, Box, Button, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { useTabParam } from '../../lib/useTabParam';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DataTable, type Column } from '../../components/DataTable';
import { Money, Qty } from '../../components/Money';
import { EmptyState } from '../../components/states';
import { businessDate, dateTime, money } from '../../lib/format';
import { purchaseOrderDoc, WhatsappA4, WhatsappHistory } from '../whatsapp';
import { AttachmentsPanel } from '../attachments';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  purchaseApi,
  useGoodsReceipts,
  usePurchaseOrder,
  usePurchasesMutation,
  useSupplierInvoices,
  type GoodsReceiptSummary,
  type PurchaseOrder,
  type PurchaseOrderLine,
  type SupplierInvoiceSummary,
} from './api';
import {
  BillStatusChip,
  DetailSkeleton,
  DocHeader,
  ErrorState,
  Facts,
  OrderStatusChip,
  ProgressMeter,
  ReasonDialog,
  ReceiptStatusChip,
  Section,
  SummaryCard,
  SummaryGrid,
  TotalsRows,
} from './components/ui';
import { compare } from './lib/decimal';
import { hasUnbilled, lineProgress, orderProgress } from './lib/progress';
import { MoreMenu } from '../../components/MoreMenu';
import { useLinked } from '../../components/useLinked';
import { useLinkedPayments } from '../../components/useLinkedPayments';

type DetailTab = 'items' | 'receipts' | 'bills';

export function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const order = usePurchaseOrder(id);
  if (order.isLoading) return <DetailSkeleton />;
  if (order.error || !order.data)
    return <ErrorState error={order.error} onRetry={() => void order.refetch()} />;
  return <OrderDetail po={order.data} />;
}

function OrderDetail({ po }: { po: PurchaseOrder }) {
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const [tab, setTab] = useTabParam<DetailTab>(['items', 'receipts', 'bills']);
  const [dialog, setDialog] = useState<'confirm' | 'cancel' | 'close' | null>(null);

  const receipts = useGoodsReceipts({ purchaseOrderId: po.id, pageSize: 50 });
  const bills = useSupplierInvoices({ purchaseOrderId: po.id, pageSize: 50 });
  const linkedReceipts = useLinked('receipts', { purchaseOrderId: po.id });
  const linkedBills = useLinked('bills', { purchaseOrderId: po.id });
  const payments = useLinkedPayments({ purchaseOrderId: po.id });
  const progress = orderProgress(po.lines);
  const unbilled = hasUnbilled(po.lines);
  const open = po.status === 'CONFIRMED' || po.status === 'PARTIALLY_RECEIVED';
  const touched = po.lines.some(
    (l) => compare(l.qtyReceived, '0') > 0 || compare(l.qtyBilled, '0') > 0,
  );

  const confirm = usePurchasesMutation(() => purchaseApi.confirmOrder(po.id, po.version));
  const cancel = usePurchasesMutation((reason: string) =>
    purchaseApi.cancelOrder(po.id, po.version, reason),
  );
  const close = usePurchasesMutation((reason: string) =>
    purchaseApi.closeOrder(po.id, po.version, reason),
  );

  const actions = (
    <>
      <WhatsappA4
        type="purchase_order"
        id={po.id}
        number={po.number}
        contact={po.contact}
        vars={{ amount: money(po.total, format), date: businessDate(po.docDate, format) }}
        doc={() => purchaseOrderDoc(po, format)}
      />
      {po.status === 'DRAFT' && can('purchases.create') && (
        <Button
          variant="outlined"
          startIcon={<EditRounded />}
          component={RouterLink}
          to={`/purchases/orders/${po.id}/edit`}
        >
          Edit
        </Button>
      )}
      {(po.status === 'DRAFT' || (open && !touched)) && can('purchases.cancel') && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<BlockRounded />}
          onClick={() => setDialog('cancel')}
        >
          Cancel order
        </Button>
      )}
      {open && touched && can('purchases.cancel') && (
        <Button variant="outlined" startIcon={<LockRounded />} onClick={() => setDialog('close')}>
          Close short
        </Button>
      )}
      {unbilled && po.status !== 'CANCELLED' && can('purchases.bill') && (
        <Button
          variant={open ? 'outlined' : 'contained'}
          startIcon={<ReceiptLongRounded />}
          component={RouterLink}
          to={`/purchases/bills/new?orderId=${po.id}`}
        >
          Record bill
        </Button>
      )}
      {open && can('purchases.receive') && (
        <Button
          variant="contained"
          startIcon={<MoveToInboxRounded />}
          component={RouterLink}
          to={`/purchases/receipts/new?orderId=${po.id}`}
        >
          Receive goods
        </Button>
      )}
      {po.status === 'DRAFT' && can('purchases.confirm') && (
        <Button
          variant="contained"
          startIcon={<CheckCircleRounded />}
          onClick={() => setDialog('confirm')}
        >
          Confirm order
        </Button>
      )}
    </>
  );

  return (
    <Box>
      <DocHeader
        back={{ to: '/purchases', label: 'Purchases' }}
        eyebrow="Purchase order"
        title={po.number}
        chips={<OrderStatusChip status={po.status} />}
        subtitle={
          <>
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {po.contact.name}
            </Box>
            {' · '}
            {businessDate(po.docDate, format)}
            {po.reference ? ` · Ref ${po.reference}` : ''}
          </>
        }
        actions={
          <>
            {actions}
            <MoreMenu
              linked={[
                { ...linkedReceipts, allTo: '?tab=receipts' },
                { ...linkedBills, allTo: '?tab=bills' },
                payments,
              ]}
              onDuplicate={
                can('purchases.create')
                  ? () => void navigate('/purchases/orders/new', { state: { copy: po } })
                  : undefined
              }
              exportModel="purchase_order"
              exportId={po.id}
            />
          </>
        }
      />

      {po.status === 'CANCELLED' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Cancelled {dateTime(po.cancelledAt, format)}
          {po.cancelReason ? ` — ${po.cancelReason}` : ''}
        </Alert>
      )}
      {po.status === 'CLOSED' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Closed short {dateTime(po.closedAt, format)}
          {po.cancelReason ? ` — ${po.cancelReason}` : ''}. The rest will not be received.
        </Alert>
      )}

      <SummaryGrid>
        <SummaryCard label="Order total" icon={<PaymentsRounded />}>
          <Money value={po.total} />
          {compare(po.taxTotal, '0') > 0 && (
            <Typography variant="caption" color="text.secondary" component="div">
              incl. <Money value={po.taxTotal} /> tax
            </Typography>
          )}
        </SummaryCard>
        <SummaryCard label="Received" icon={<InventoryRounded />} accent="success">
          <ProgressMeter
            label={
              progress.outstandingLines > 0
                ? `${String(progress.outstandingLines)} ${progress.outstandingLines === 1 ? 'line' : 'lines'} to come`
                : 'All lines in'
            }
            value={progress.receivedPct}
            color="success"
          />
        </SummaryCard>
        <SummaryCard label="Billed" icon={<DescriptionRounded />} accent="info">
          <ProgressMeter
            label={unbilled ? 'Received, not billed yet' : 'Up to date'}
            value={progress.billedPct}
            color="info"
          />
        </SummaryCard>
        <SummaryCard label="Delivery" icon={<EventRounded />} accent="warning">
          {po.expectedDate ? businessDate(po.expectedDate, format) : 'No date'}
          <Typography variant="caption" color="text.secondary" component="div">
            to {po.location.name}
          </Typography>
        </SummaryCard>
      </SummaryGrid>

      <Tabs
        value={tab}
        onChange={(_, v: DetailTab) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab value="items" label={`Items (${String(po.lines.length)})`} />
        <Tab
          value="receipts"
          label={`Receipts${receipts.data ? ` (${String(receipts.data.page.total)})` : ''}`}
        />
        <Tab
          value="bills"
          label={`Bills${bills.data ? ` (${String(bills.data.page.total)})` : ''}`}
        />
      </Tabs>

      {tab === 'items' && (
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 320px' },
            alignItems: 'start',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <OrderLinesTable po={po} />
          </Box>
          <Box>
            <Section title="Summary">
              <TotalsRows
                rows={[
                  { label: 'Subtotal', value: po.subtotal },
                  ...(compare(po.discountTotal, '0') > 0
                    ? [{ label: 'Discounts', value: po.discountTotal, negative: true }]
                    : []),
                  ...(compare(po.taxTotal, '0') > 0 ? [{ label: 'Tax', value: po.taxTotal }] : []),
                ]}
                total={po.total}
              />
              {po.pricesIncludeTax && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                  Prices include tax.
                </Typography>
              )}
            </Section>
            <Section title="Details">
              <Facts
                items={[
                  ['Supplier', po.contact.name],
                  ...(po.contact.phone ? [['Phone', po.contact.phone] as [string, string]] : []),
                  ['Deliver to', po.location.name],
                  ['Created', dateTime(po.createdAt, format)],
                  ...(po.confirmedAt
                    ? [['Confirmed', dateTime(po.confirmedAt, format)] as [string, string]]
                    : []),
                ]}
              />
              {po.notes && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2, whiteSpace: 'pre-wrap' }}
                >
                  {po.notes}
                </Typography>
              )}
            </Section>
          </Box>
        </Box>
      )}

      {tab === 'receipts' && (
        <DataTable<GoodsReceiptSummary>
          columns={[
            { key: 'n', header: 'Receipt', render: (r) => <strong>{r.number}</strong> },
            { key: 's', header: 'Status', render: (r) => <ReceiptStatusChip status={r.status} /> },
            { key: 'd', header: 'Date', render: (r) => businessDate(r.docDate, format) },
            {
              key: 'ref',
              header: 'Delivery note',
              hideBelow: 'md',
              render: (r) => r.supplierRef ?? '—',
            },
            ...(can('products.view_cost')
              ? [
                  {
                    key: 'v',
                    header: 'Stock value',
                    align: 'right' as const,
                    render: (r: GoodsReceiptSummary) => <Money value={r.totalCost} strong />,
                  },
                ]
              : []),
          ]}
          rows={receipts.data?.data}
          rowKey={(r) => r.id}
          loading={receipts.isLoading}
          onRowClick={(r) => void navigate(`/purchases/receipts/${r.id}`)}
          empty={
            <EmptyState
              icon={<InventoryRounded />}
              title="Nothing received yet"
              body="When the delivery arrives, receive it here — partly or all at once."
              action={
                open && can('purchases.receive')
                  ? {
                      label: 'Receive goods',
                      onClick: () => void navigate(`/purchases/receipts/new?orderId=${po.id}`),
                    }
                  : undefined
              }
            />
          }
        />
      )}

      {tab === 'bills' && (
        <DataTable<SupplierInvoiceSummary>
          columns={[
            { key: 'n', header: 'Bill', render: (r) => <strong>{r.number}</strong> },
            {
              key: 's',
              header: 'Status',
              render: (r) => (
                <BillStatusChip
                  status={r.status}
                  paymentStatus={r.paymentStatus}
                  overdue={r.overdue}
                />
              ),
            },
            {
              key: 'sin',
              header: 'Supplier #',
              hideBelow: 'md',
              render: (r) => r.supplierInvoiceNumber ?? '—',
            },
            { key: 'd', header: 'Due', render: (r) => businessDate(r.dueDate, format) },
            {
              key: 't',
              header: 'Total',
              align: 'right',
              render: (r) => <Money value={r.total} strong />,
            },
          ]}
          rows={bills.data?.data}
          rowKey={(r) => r.id}
          loading={bills.isLoading}
          onRowClick={(r) => void navigate(`/purchases/bills/${r.id}`)}
          empty={
            <EmptyState
              icon={<ReceiptLongRounded />}
              title="No bills yet"
              body="Record the supplier’s invoice once goods have been received."
              action={
                unbilled && can('purchases.bill')
                  ? {
                      label: 'Record bill',
                      onClick: () => void navigate(`/purchases/bills/new?orderId=${po.id}`),
                    }
                  : undefined
              }
            />
          }
        />
      )}

      <ConfirmDialog
        open={dialog === 'confirm'}
        title={`Confirm ${po.number}?`}
        body={
          <>
            The order to {po.contact.name} for {format.currencyCode} <Money value={po.total} />{' '}
            becomes final and can be received. Nothing changes in stock or the accounts until goods
            arrive.
          </>
        }
        confirmLabel="Confirm order"
        onConfirm={() =>
          confirm
            .mutateAsync(undefined)
            .then(() => notify.success(`${po.number} confirmed`))
            .catch((e: unknown) => notify.error(e))
        }
        onClose={() => setDialog(null)}
      />
      <ReasonDialog
        open={dialog === 'cancel'}
        title={`Cancel ${po.number}?`}
        body="The order can no longer be received or billed. This can’t be undone."
        confirmLabel="Cancel order"
        onConfirm={(reason) =>
          cancel.mutateAsync(reason).then(() => notify.success(`${po.number} cancelled`))
        }
        onClose={() => setDialog(null)}
      />
      <ReasonDialog
        open={dialog === 'close'}
        title={`Close ${po.number} short?`}
        body="Whatever hasn’t arrived will not be received. You can still bill what came in."
        confirmLabel="Close order"
        danger={false}
        reasonRequired={false}
        onConfirm={(reason) =>
          close.mutateAsync(reason).then(() => notify.success(`${po.number} closed`))
        }
        onClose={() => setDialog(null)}
      />
      <AttachmentsPanel recordType="PURCHASE_ORDER" recordId={po.id} sx={{ mt: 3 }} />
      <WhatsappHistory documentType="purchase_order" documentId={po.id} sx={{ mt: 3 }} />
      <EmailHistory documentType="purchase_order" documentId={po.id} sx={{ mt: 3 }} />
    </Box>
  );
}

function LineBar({ value, color }: { value: number; color: 'success' | 'info' }) {
  return (
    <LinearProgress
      variant="determinate"
      value={Math.min(100, value)}
      color={color}
      sx={{ height: 4, mt: 0.75, bgcolor: 'action.hover', maxWidth: 120, ml: 'auto' }}
    />
  );
}

function OrderLinesTable({ po }: { po: PurchaseOrder }) {
  const showProgress = po.status !== 'DRAFT' && po.status !== 'CANCELLED';
  const columns: Column<PurchaseOrderLine>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (l) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {l.description}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {l.sku}
            {l.factor !== '1' && Number(l.factor) !== 1
              ? ` · ${l.unitSymbol} of ${String(Number(l.factor))}`
              : ''}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'qty',
      header: 'Ordered',
      align: 'right',
      render: (l) => <Qty value={l.qty} unit={l.unitSymbol} />,
    },
    ...(showProgress
      ? [
          {
            key: 'rec',
            header: 'Received',
            align: 'right' as const,
            hideBelow: 'sm' as const,
            render: (l: PurchaseOrderLine) => {
              const p = lineProgress(l);
              return (
                <Box>
                  <Qty value={p.received} />
                  <LineBar value={p.receivedPct} color="success" />
                </Box>
              );
            },
          },
          {
            key: 'billed',
            header: 'Billed',
            align: 'right' as const,
            hideBelow: 'md' as const,
            render: (l: PurchaseOrderLine) => {
              const p = lineProgress(l);
              return (
                <Box>
                  <Qty value={p.billed} />
                  <LineBar value={p.billedPct} color="info" />
                </Box>
              );
            },
          },
          {
            key: 'out',
            header: 'Left',
            align: 'right' as const,
            hideBelow: 'sm' as const,
            render: (l: PurchaseOrderLine) =>
              compare(l.qtyToReceive, '0') > 0 ? (
                <Typography variant="body2" sx={{ fontWeight: 650, color: 'warning.main' }}>
                  <Qty value={l.qtyToReceive} />
                </Typography>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  —
                </Typography>
              ),
          },
        ]
      : []),
    {
      key: 'price',
      header: 'Unit cost',
      align: 'right',
      hideBelow: 'lg',
      render: (l) => (
        <Box>
          <Money value={l.unitPrice} />
          {compare(l.discountAmount, '0') > 0 && (
            <Typography variant="caption" color="success.main" component="div">
              − <Money value={l.discountAmount} />
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (l) => <Money value={l.lineTotal} strong />,
    },
  ];
  return <DataTable columns={columns} rows={po.lines} rowKey={(l) => l.id} />;
}
